import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { getStripeSearchHrefFromPath } from '@share/utils';

import { BlueButton } from '../blue-button';

import './style.scss';

/**
 * `'error'`: the resume call failed (we have a bookingId but couldn't confirm).
 * `'go-to-search'`: there's no booking to resume from this URL (e.g., F5 after a successful booking,
 * once resume params have been stripped).
 *
 * Both modes always navigate the user to the vertical's search page — only the heading/copy differs.
 * `'back-to-previous'` is preserved as an alias of `'error'` for backwards compatibility.
 */
export type StripeResumeErrorMode = 'error' | 'go-to-search' | 'back-to-previous';

interface IStripeResumeErrorProps {
  mode?: StripeResumeErrorMode;
}

/** Full-page error shown when a Stripe express booking resume can't render its expected content. */
function StripeResumeErrorComponent(props: RouteComponentProps & IStripeResumeErrorProps) {
  const mode: StripeResumeErrorMode = props.mode ?? 'error';
  const isNoBookingMode = mode === 'go-to-search';

  const handleAction = (): void => {
    const { pathname } = props.history.location;
    props.history.push(getStripeSearchHrefFromPath(pathname));
  };

  const titleId = isNoBookingMode
    ? 'stripe.resume.no_booking.title'
    : 'stripe.resume.error.title';
  const descriptionId = isNoBookingMode
    ? 'stripe.resume.no_booking.description'
    : 'stripe.resume.error.description';

  return (
    <div className="stripe-resume-error">
      <FontAwesomeIcon className="stripe-resume-error__icon" icon={faTriangleExclamation} />
      <h4 className="stripe-resume-error__title secondary-font">
        <FormattedMessage id={titleId} />
      </h4>
      <p className="stripe-resume-error__description">
        <FormattedMessage id={descriptionId} />
      </p>
      <BlueButton className="stripe-resume-error__back" onClick={handleAction}>
        <FormattedMessage id="stripe.resume.back_to_search" />
      </BlueButton>
    </div>
  );
}

export const StripeResumeError = withRouter(StripeResumeErrorComponent);
